import { useEffect, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import styled from '@mint-lib/styled';
import DialogM, { DialogProps } from '@mui/material/Dialog';

import Box from '../Box/Box.jsx';
import Button, { type ButtonProps } from '../Button/Button.jsx';
import Icon from '../Icon/Icon.jsx';
import IconButton from '../IconButton/IconButton.jsx';
import Typography from '../Typography/Typography.jsx';

interface BtnProps extends ButtonProps {
  label?: string;
}

export interface PremiumSuccessModalProps extends DialogProps {
  /**
   * If `true`, the PremiumSuccessModal is open.
   */
  open: DialogProps['open'];
  /**
   * If true, hitting escape will not fire the onClose callback.
   */
  /**  */
  disableEscapeKeyDown?: boolean;
  /**
   *
   */
  children?: React.ReactNode;
  /**
   * The component used to render the body of the dialog.
   */
  PaperComponent?: DialogProps['PaperComponent'];
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   * See the `sx` page for more details. https://mui.com/system/the-sx-prop/
   */
  /**  */
  sx?: DialogProps['sx'];
  /**
   * Title of a dialog
   */
  title: string;

  /**
   * Object with button props for submit button
   */
  submitButtonProps?: BtnProps;
  /**
   * Object with button props for additional action button
   */
  additionalActionButtonProps?: BtnProps;
  /**
   * If true, close button will be shown
   */
  showCloseButton?: boolean;
  /**
   * Callback to control modal closing
   */
  onClose: (...args: any) => void;
  /**
   * Pass your own submit button
   */
  submitButton?: React.ReactNode;
  /**
   * Description of a dialog
   */
  description?: string | React.ReactNode;
  /**
   * Inline if description one line, multiline - if more than one line
   */
  variant?: 'inline' | 'multiline';
}

function PremiumSuccessModal({
  open,
  TransitionProps,
  title,
  onClose,
  submitButtonProps,
  submitButton,
  description,
  variant = 'inline',
  additionalActionButtonProps,
  showCloseButton = true,
  ...props
}: PremiumSuccessModalProps) {
  const { t } = useTranslation('@myn/mui');

  const [isTransitionFinished, setIsTransitionFinished] = useState(false);
  const [isContentDisplayed, setIsContentDisplayed] = useState(false);
  const [isContentVisible, setIsContentVisible] = useState(false);

  useEffect(() => {
    let timeouts: any[] = [];

    if (open) {
      setIsTransitionFinished(false);
      setIsContentDisplayed(false);
      setIsContentVisible(false);

      timeouts = [
        setTimeout(() => setIsTransitionFinished(true), 2000),
        setTimeout(() => setIsContentDisplayed(true), 2700),
        setTimeout(() => setIsContentVisible(true), 2850),
      ];
    }

    return () => {
      timeouts.forEach((timeout) => clearTimeout(timeout));
    };
  }, [open]);

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      variant={variant}
      {...props}
    >
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
        {showCloseButton && (
          <IconButton
            aria-label="Close dialog"
            onClick={onClose}
            icon="Close"
            size="small"
            variant="icon"
            color="secondary"
          />
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'fit-content',
        }}
      >
        <Icon
          component="SuccessCheckAnimated"
          sx={{
            width: isTransitionFinished ? '120px' : '240px',
            height: isTransitionFinished ? '120px' : '240px',
            transition: 'width 0.8s ease-out, height 0.8s ease-out',
          }}
        />
      </Box>
      <Box
        sx={{
          height: isContentDisplayed ? '100%' : '0',
          transition: isContentDisplayed ? 'height 0.4s ease-out' : 'none',
        }}
      >
        <Box
          sx={{
            display: isContentDisplayed ? 'flex' : 'none',
            flexDirection: 'column',
            height: '100%',
            opacity: isContentVisible ? 1 : 0,
            transition: isContentVisible ? 'opacity 0.9s ease-out' : 'none',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <Typography
              variant="h3"
              align="center"
              color={(theme) => theme.palette.text.primary}
            >
              {title}
            </Typography>
            {description ? (
              <Typography
                variant="bodyLong01"
                align="center"
                color={(theme) => theme.palette.text.secondary}
              >
                {description}
              </Typography>
            ) : null}
          </Box>

          <Box sx={{ display: 'flex', gap: '8px' }}>
            {additionalActionButtonProps && (
              <Button
                {...additionalActionButtonProps}
                variant={additionalActionButtonProps.variant || 'text'}
                color={additionalActionButtonProps.color || 'primary'}
                size="large"
                fullWidth
              >
                {additionalActionButtonProps.label || t('Additional action')}
              </Button>
            )}
            {submitButton ? (
              submitButton
            ) : (
              <Button
                variant={submitButtonProps?.variant || 'contained'}
                color={submitButtonProps?.color || 'primary'}
                size="large"
                fullWidth
                {...submitButtonProps}
              >
                {submitButtonProps?.label || t('Save')}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </StyledDialog>
  );
}

export default PremiumSuccessModal;

const StyledDialog = styled(DialogM, {
  shouldForwardProp: (prop) => prop !== 'inline',
})<{
  variant?: 'inline' | 'multiline';
}>`
  .MuiBackdrop-root {
    background-color: ${({ theme }) => theme.palette.ui.overlay};
  }
  .MuiDialog-paper {
    box-shadow: none;
    border-radius: 4px;
    width: 360px;
    height: ${({ variant }) => (variant === 'inline' ? '332px' : '352px')};
    background-color: ${({ theme }) => theme.palette.ui.primary};
    padding: 16px;
    overflow: hidden;
  }
`;
